import { Directive, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { isPinataUnoptimizable, markPinataUnoptimizable, stripPinataImageParams } from '../utils/pinata-thumbnail.util';

export const DEFAULT_NFT_FALLBACK = 'assets/images/default-nft.svg';

/**
 * How long an <img> may sit with no src of its own before the placeholder is shown.
 *
 * List and detail templates bind `[src]` to data that is still loading, and Angular
 * renders `src=""` for a null binding — which the browser reports as an error even
 * though nothing was ever requested. Falling back immediately is what made the
 * placeholder flash on screen and then get replaced by the real image a moment later.
 * Waiting first means the common case (data arrives) never shows a placeholder at all,
 * while an asset that genuinely has no media still ends up with one.
 */
export const EMPTY_SRC_GRACE_MS = 1200;

/**
 * Class carried by an image until it has loaded, styled globally as a shimmer.
 *
 * The gateway can take a second or two before it starts sending an image, and the box sat
 * blank for all of it. The shimmer shows the image is on its way.
 */
export const IMAGE_SHIMMER_CLASS = 'img-shimmer';

/**
 * Standard <img> treatment: lazy loading, async decoding, and a safe fallback.
 *
 * Replaces the inconsistent inline variants that were spread across the templates
 * (`onerror="this.src='assets/images/default-nft.svg'"`, three `visibility:hidden`
 * handlers, a currency.png handler, and one broken handler that never assigned this.src).
 *
 * The error path runs in three steps, in this order:
 *
 *  1. **No src yet** — the binding is still empty, so nothing has actually failed.
 *     The placeholder is deferred by {@link EMPTY_SRC_GRACE_MS}, and skipped entirely
 *     if a real URL arrives in the meantime. This is what keeps a slow-loading card
 *     from flashing the placeholder before its image appears.
 *  2. **Optimized Pinata URL failed** — retry with the `img-*` params stripped.
 *     The gateway optimizer rejects formats it cannot decode (AVIF answers 415), while
 *     the plain URL serves the file at 200, and the format cannot be told from the URL.
 *     Without this, every AVIF-sourced asset shows the placeholder instead of its image.
 *     Once the original has loaded, the file is remembered for the session, so the next
 *     time this element (or any other) is given it, the original is used straight away
 *     rather than waiting out the optimizer's rejection again.
 *  3. **The image is genuinely unreachable** — show the placeholder.
 *
 * Throughout all three, the element is hidden from the moment it first errors until
 * something actually renders. Otherwise the browser paints its broken-image icon and
 * the alt text ("nft") over the card for as long as the retry takes — which is the
 * state users were seeing flash by before the real image appeared.
 *
 * Two behaviours matter for the list pages:
 *  - the native `load` event still fires after a swap, so the templates'
 *    `(load)="imageLoading = false"` bindings keep clearing their spinners;
 *  - a swap can never loop: only an optimized URL is ever retried, an original that has
 *    already failed on this element is not retried again, and the placeholder failing on
 *    itself is ignored.
 *
 * Until something has loaded, the element carries {@link IMAGE_SHIMMER_CLASS}. It is set
 * again whenever the bound URL changes - a detail page swaps the hero image on the same
 * element - and skipped for an image the browser already has, so cached images never
 * flicker.
 *
 * Usage:
 *   <img [appImageFallback]="'assets/images/default-nft.svg'" src="...">
 *   <img appImageFallback [eagerLoad]="true" src="...">   <!-- above-the-fold hero -->
 */
@Directive({
    selector: 'img[appImageFallback]',
    standalone: true
})
export class ImageFallbackDirective implements OnInit, OnDestroy {

    /** Fallback source. Empty/omitted uses the default NFT placeholder. */
    @Input() appImageFallback: string | null | undefined = DEFAULT_NFT_FALLBACK;

    /**
     * Opt out of lazy loading for above-the-fold images (detail-page heroes, the header
     * logo). Lazy-loading those regresses LCP rather than improving it.
     */
    @Input() eagerLoad = false;

    /** The original URL currently being tried after its optimized form failed. */
    private pendingOriginal: string | null = null;

    /**
     * An original that has already failed on this element, so it is not retried again.
     * Tracked per URL rather than as a single "already swapped" flag because `*ngFor` and
     * detail pages reuse these elements: a different asset still gets its own retry.
     */
    private brokenOriginal: string | null = null;

    /** Watches the src the template binds, which can change without the element changing. */
    private srcObserver: MutationObserver | null = null;

    private emptySrcTimer: ReturnType<typeof setTimeout> | null = null;

    /** Whether this directive is the one that hid the element, so it only clears its own style. */
    private hiddenWhileResolving = false;

    /** Registered manually rather than with @HostListener: a host binding would run change
     *  detection on every one of the hundreds of images a list page loads successfully. */
    private readonly onLoad = (): void => {
        this.stopShimmer();
        this.reveal();
        if (this.pendingOriginal && this.currentSrc() === this.pendingOriginal) {
            markPinataUnoptimizable(this.pendingOriginal);
        }
        this.pendingOriginal = null;
    };

    constructor(
        private readonly host: ElementRef<HTMLImageElement>,
        private readonly zone: NgZone
    ) {}

    ngOnInit(): void {
        const img = this.host.nativeElement;

        // Only set when the template has not already made an explicit choice.
        if (!img.hasAttribute('loading')) {
            img.loading = this.eagerLoad ? 'eager' : 'lazy';
        }
        if (!img.hasAttribute('decoding')) {
            img.decoding = 'async';
        }
        if (this.eagerLoad && !img.hasAttribute('fetchpriority')) {
            img.setAttribute('fetchpriority', 'high');
        }

        // Outside Angular for the same reason as the load listener: a list page holds hundreds
        // of these, and a src change needs a class toggle, not a change-detection pass.
        this.zone.runOutsideAngular(() => {
            img.addEventListener('load', this.onLoad);
            if (typeof MutationObserver !== 'undefined') {
                this.srcObserver = new MutationObserver(() => this.onSrcChange());
                this.srcObserver.observe(img, { attributes: true, attributeFilter: ['src'] });
            }
        });

        // A static src is already in place before this runs; a bound one arrives later and is
        // picked up by the observer.
        this.onSrcChange();
    }

    ngOnDestroy(): void {
        this.clearEmptySrcTimer();
        this.srcObserver?.disconnect();
        this.host.nativeElement.removeEventListener('load', this.onLoad);
    }

    /**
     * Reacts to a new src: goes straight to the original for a file the optimizer is known to
     * refuse, and shows the shimmer until the new image has loaded.
     */
    private onSrcChange(): void {
        const img = this.host.nativeElement;
        const src = this.currentSrc();

        if (!src || src === this.fallbackSrc()) {
            this.stopShimmer();
            return;
        }

        const original = stripPinataImageParams(src);
        if (typeof original === 'string' && original !== src && isPinataUnoptimizable(src)) {
            // Set directly rather than through swapSrc: nothing has failed on this element, so
            // a lazy image stays lazy. Changing src re-runs this for the original.
            img.src = original;
            return;
        }

        if (img.complete && img.naturalWidth > 0) {
            this.stopShimmer();
            return;
        }
        img.classList.add(IMAGE_SHIMMER_CLASS);
    }

    @HostListener('error')
    onError(): void {
        const current = this.currentSrc();
        const fallback = this.fallbackSrc();

        // Nothing renderable is on screen right now: keep the broken-image icon and the
        // alt text out of the card until this resolves one way or the other.
        this.hide();

        // The placeholder itself failed to load — there is nothing better to show, and
        // the element stays hidden rather than showing a broken icon.
        if (current === fallback) {
            this.stopShimmer();
            return;
        }

        // Nothing was really requested: the binding is empty because its data has not
        // arrived. Give it a moment rather than painting a placeholder over it.
        if (!current) {
            this.scheduleEmptySrcFallback();
            return;
        }

        // A real URL failed, so any pending "still empty" timer is moot.
        this.clearEmptySrcTimer();

        const original = stripPinataImageParams(current);
        if (typeof original === 'string' && original !== current) {
            // An optimized URL failed: try its original, unless that has already failed here.
            if (original !== this.brokenOriginal) {
                this.pendingOriginal = original;
                this.swapSrc(original);
                return;
            }
        } else if (current === this.pendingOriginal) {
            // The retry itself failed: the file is unreachable, not just unoptimizable.
            this.brokenOriginal = current;
        }

        this.pendingOriginal = null;
        this.swapSrc(fallback);
    }

    private currentSrc(): string {
        return (this.host.nativeElement.getAttribute('src') || '').trim();
    }

    private fallbackSrc(): string {
        return this.appImageFallback || DEFAULT_NFT_FALLBACK;
    }

    /**
     * Shows the placeholder only if the element is *still* without a src once the grace
     * period has passed. Runs outside Angular: the swap is a DOM write, and a list page
     * can hold hundreds of these images — one change-detection pass per timer would cost
     * far more than the swap itself.
     */
    private scheduleEmptySrcFallback(): void {
        if (this.emptySrcTimer !== null) return;

        this.zone.runOutsideAngular(() => {
            this.emptySrcTimer = setTimeout(() => {
                this.emptySrcTimer = null;
                if (this.currentSrc()) return;
                this.swapSrc(this.fallbackSrc());
            }, EMPTY_SRC_GRACE_MS);
        });
    }

    private clearEmptySrcTimer(): void {
        if (this.emptySrcTimer === null) return;
        clearTimeout(this.emptySrcTimer);
        this.emptySrcTimer = null;
    }

    private hide(): void {
        if (this.hiddenWhileResolving) return;
        this.hiddenWhileResolving = true;
        // visibility, not display: the box keeps its space, so nothing on the card moves.
        this.host.nativeElement.style.visibility = 'hidden';
    }

    private stopShimmer(): void {
        this.host.nativeElement.classList.remove(IMAGE_SHIMMER_CLASS);
    }

    /** Something rendered — drop only the style this directive set. */
    private reveal(): void {
        if (!this.hiddenWhileResolving) return;
        this.hiddenWhileResolving = false;
        this.host.nativeElement.style.visibility = '';
    }

    /** A lazy image that has already errored must load its replacement immediately. */
    private swapSrc(src: string): void {
        const img = this.host.nativeElement;
        if (src === this.fallbackSrc()) this.stopShimmer();
        img.loading = 'eager';
        img.src = src;
    }
}
